import BigNumber from 'bignumber.js'
import masterchefABI from 'config/abi/ZenMaster.json'
import slaveFarmABI from 'config/abi/slaveFarmABI.json'
import erc20 from 'config/abi/erc20.json'
import { getAddress, getMasterChefAddress } from 'utils/addressHelpers'
import { BIG_TEN, BIG_ZERO } from 'utils/bigNumber'
import multicall from 'utils/multicall'
import { BLOCK_TIME } from 'config'
import rewardToken from 'config/constants/rewardToken'
import { Farm, slaveFarmData, SerializedBigNumber } from '../types'

type PublicFarmData = {
  tokenAmountMc: SerializedBigNumber
  quoteTokenAmountMc: SerializedBigNumber
  tokenAmountTotal: SerializedBigNumber
  quoteTokenAmountTotal: SerializedBigNumber
  lpTotalInQuoteToken: SerializedBigNumber
  lpTotalSupply: SerializedBigNumber
  tokenPriceVsQuote: SerializedBigNumber
  poolWeight: SerializedBigNumber
  multiplier: string
  slaveFarms?: slaveFarmData[]
}

// determine if the farm or slave farm is distributing rewards
function areRewardsActive(startBlock, lastRewardBlock) {

  if(startBlock > 0) {
    return false
  }
  return true
}

const fetchFarm = async (farm: Farm): Promise<PublicFarmData> => {
  const { pid, lpAddresses, token, quoteToken, slaveFarms } = farm
  const lpAddress = getAddress(lpAddresses)
  const calls = [
    // Balance of token in the LP contract
    {
      address: getAddress(token.address),
      name: 'balanceOf',
      params: [lpAddress],
    },
    // Balance of quote token on LP contract
    {
      address: getAddress(quoteToken.address),
      name: 'balanceOf',
      params: [lpAddress],
    },
    // Balance of LP tokens in the master chef contract
    {
      address: lpAddress,
      name: 'balanceOf',
      params: [getMasterChefAddress()],
    },
    // Balance of Reward tokens in the master chef contract
    {
      address: rewardToken.address,
      name: 'balanceOf',
      params: [getMasterChefAddress()],
    },
    // Total supply of LP tokens
    {
      address: lpAddress,
      name: 'totalSupply',
    },
    // Token decimals
    {
      address: getAddress(token.address),
      name: 'decimals',
    },
    // Quote token decimals
    {
      address: getAddress(quoteToken.address),
      name: 'decimals',
    },
  ]

  const [tokenBalanceLP, quoteTokenBalanceLP, lpTokenBalanceMC, rewardBalanceMC, lpTotalSupply, tokenDecimals, quoteTokenDecimals] =
    await multicall(erc20, calls)

  // Ratio in % of LP tokens that are staked in the MC, vs the total number in circulation
  const lpTokenRatio = new BigNumber(lpTokenBalanceMC).div(new BigNumber(lpTotalSupply))

  // Raw amount of token in the LP, including those not staked
  const tokenAmountTotal = new BigNumber(tokenBalanceLP).div(BIG_TEN.pow(tokenDecimals))
  const quoteTokenAmountTotal = new BigNumber(quoteTokenBalanceLP).div(BIG_TEN.pow(quoteTokenDecimals))

  // Amount of token in the LP that are staked in the MC (i.e amount of token * lp ratio)
  const tokenAmountMc = tokenAmountTotal.times(lpTokenRatio)
  const quoteTokenAmountMc = quoteTokenAmountTotal.times(lpTokenRatio)

  // Total staked in LP, in quote token value
  const lpTotalInQuoteToken = quoteTokenAmountMc.times(new BigNumber(2))
  // Only make masterchef calls if farm has pid
  const [info, totalAllocPoint, rawSlaveFarmAddresses] =
    pid || pid === 0
      ? await multicall(masterchefABI, [
          {
            address: getMasterChefAddress(),
            name: 'poolInfo',
            params: [pid],
          },
          {
            address: getMasterChefAddress(),
            name: 'totalAllocPoint',
          },
          {
            address: getMasterChefAddress(), 
            name: 'getSlaveFarms',
          },
        ])
      : [null, null, null]


  const slaveFarmAddresses = rawSlaveFarmAddresses[0]
  const allocPoint = info ? new BigNumber(info.allocPoint?._hex) : BIG_ZERO
  const poolWeight = totalAllocPoint ? allocPoint.div(new BigNumber(totalAllocPoint)) : BIG_ZERO
  // Fetch slave farm data
  let slaveFarmDataArray: slaveFarmData[] = []
 //  const slaveFarmAddresses =[]

  if (slaveFarmAddresses && slaveFarmAddresses.length > 0) {
    const slaveCalls = slaveFarmAddresses.flatMap((slaveFarmAddress: string) => [
      {
        address: slaveFarmAddress,
        name: 'totalAllocPoint',
      },
      {
        address: slaveFarmAddress,
        name: 'poolInfo',
        params: [pid],
      },
    ])

    const slaveResults = await multicall(slaveFarmABI, slaveCalls)

    slaveFarmDataArray = slaveFarmAddresses.map((slaveFarmAddress: string, index: number) => ({
      slaveFarmAddress,
      poolWeight: (slaveResults[index * 2] ? 
        (slaveResults[index * 2 + 1] ? new BigNumber(slaveResults[index * 2 + 1].allocPoint?._hex) : BIG_ZERO)
        .div(new BigNumber(slaveResults[index * 2])) : BIG_ZERO).toString(),
      totalStakedBalance: slaveResults[index * 2 + 1].balanceOfThis.toString(),
    }))
  }

  return {
    tokenAmountMc: tokenAmountMc.toJSON(),
    quoteTokenAmountMc: quoteTokenAmountMc.toJSON(),
    tokenAmountTotal: tokenAmountTotal.toJSON(),
    quoteTokenAmountTotal: quoteTokenAmountTotal.toJSON(),
    lpTotalSupply: new BigNumber(lpTotalSupply).toJSON(),
    lpTotalInQuoteToken: lpTotalInQuoteToken.toJSON(),
    tokenPriceVsQuote: quoteTokenAmountTotal.div(tokenAmountTotal).toJSON(),
    poolWeight: poolWeight.toJSON(),
    multiplier: `${allocPoint.div(100).toString()}X`,
    slaveFarms: slaveFarmDataArray,
  }
}

export default fetchFarm
